import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { WIZARD_EVENTS, useWizardService } from '@bingads-webui-fluent-component/wizard';
import { FORM_STATES, useFormService } from '@bingads-webui-fluent-component/form';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { LINKEDIN_WIZARD_STEP_PATHS } from '@bingads-webui-campaign-react/linkedin-utils';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { StepHeader } from '@bingads-webui-campaign-react/linkedin-shared-components';
import { GeneratedUI } from '@bingads-webui-campaign-react/ui-generator';
import { summaryPageComponentTypes as sharedComponentTypes } from '@bingads-webui-campaign-react/summary-page';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';

import { useSummarySchema } from './use-summary-schema';
import { useSummarySchemaData } from './use-summary-schema-data';
import { CompanyPage, AudienceTargeting, AdCount, AdsReviewInfoBanner, GoalCount, GoalDetails } from './components';
import { getSummaryStepStyle } from './styles/summary-step-styles';

const UnmappedFormServerErrors = withControlErrorHandling(UnmappedFormErrors);

const CustomComponentTypes = {
  ...sharedComponentTypes,
  'ad-count': AdCount,
  'li-company-page': CompanyPage,
  'li-audience-targeting': AudienceTargeting,
  'goal-count': GoalCount,
  'goal-details': GoalDetails,
};

export const SummaryStep = ({ data, error }) => {
  const { serverErrors = [] } = error || {};

  const { send } = useWizardService();
  const { state } = useFormService();
  const { context: { saveFailed } } = state;

  const isSaving = state.matches(FORM_STATES.SAVING);

  const showAllValidationErrors = !_.isEmpty(serverErrors) || saveFailed;
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const isMultiAdModel = data && data.isMultiAdModel;

  const navigateToStep = useCallback(stepId => send(WIZARD_EVENTS.NAVIGATE_TO, { stepId }), [send]);

  const options = useMemo(() => ({
    onEditAdsClick: () => navigateToStep(isMultiAdModel ? LINKEDIN_WIZARD_STEP_PATHS.MULTI_AD_MANAGER : LINKEDIN_WIZARD_STEP_PATHS.LINKEDIN_AD),
    onEditTargetingClick: () => navigateToStep(LINKEDIN_WIZARD_STEP_PATHS.TARGETING),
    onEditBudgetClick: () => navigateToStep(LINKEDIN_WIZARD_STEP_PATHS.BUDGET),
    onEditConversionsClick: () => navigateToStep(LINKEDIN_WIZARD_STEP_PATHS.TARGETING),
  }), [navigateToStep, isMultiAdModel]);

  const schema = useSummarySchema(options);
  const schemaData = useSummarySchemaData({ data });
  const classes = useDefaultStyles(getSummaryStepStyle);

  const isLoading = useMemo(() => !data, [data]);

  return (
    <div className={classNames('linkedin-summary-step', classes.root, classes.heading)}>
      <StepHeader
        title={_TL_('Summary')}
        description={_TL_('Review your campaign details.')}
      />
      {!isLoading && (
        <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
              <UnmappedFormServerErrors className="unmapped-server-errors" />
              <GeneratedUI customComponentTypes={CustomComponentTypes} schema={schema} {...schemaData} />
              <AdsReviewInfoBanner />
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      )}
    </div>
  );
};

SummaryStep.propTypes = {
  error: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    isMultiAdModel: PropTypes.bool.isRequired,
  }).isRequired,
};
